import { Badge, Box, Button, Flex, HStack, Input, SimpleGrid, Text, Tooltip, VStack } from '@chakra-ui/react';
import { TitleBar } from '@resistapp/client/components/bars/title-bar';
import { ResistomapSelect } from '@resistapp/client/components/forms/resistomap-select';
import { envTypeToColorScheme } from '@resistapp/client/components/shared/env-badge';
import { theme } from '@resistapp/client/components/shared/theme';
import { Loading } from '@resistapp/client/components/states/loading';
import {
  useAdminSamplingPointSamples,
  useAdminSamplingPoints,
  useCountries,
  useProjects,
} from '@resistapp/client/hooks/api';
import { SampleType, friendlySampleType } from '@resistapp/common/sampling-point-types';
import type { GroupBase } from 'chakra-react-select';
import { useMemo, useState } from 'react';
import { Link, NavLink } from 'react-router-dom';

type Option = { value: string | number; label: string };

export function AdminSamplingPointsView() {
  const { data: projects } = useProjects();
  const { data: countriesByAlpha3 } = useCountries();

  const [projectOpt, setProjectOpt] = useState<Option | null>(null);
  const [envNameFilter, setEnvNameFilter] = useState('');
  const [envNameInput, setEnvNameInput] = useState('');
  const [envTypes, setSampleTypes] = useState<Option[]>([]);
  const [countries, setCountries] = useState<Option[]>([]);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState<Option>({ value: 100, label: '100' });

  const qs = useMemo(() => {
    const p = new URLSearchParams();
    if (projectOpt) p.set('projectId', String(projectOpt.value));
    if (envNameFilter.trim()) p.set('envName', envNameFilter.trim());
    if (envTypes.length) p.set('envType', envTypes.map(o => o.value).join(','));
    if (countries.length) p.set('country', countries.map(o => o.value).join(','));
    p.set('page', String(page));
    p.set('pageSize', String(pageSize.value));
    return p;
  }, [projectOpt, envNameFilter, envTypes, countries, page, pageSize]);

  const { data, isPending, error } = useAdminSamplingPoints(qs);

  const projectOptions = useMemo<Option[]>(
    () =>
      (projects || [])
        .slice()
        .sort((a, b) => {
          const tb = new Date(b.createdAt).getTime();
          const ta = new Date(a.createdAt).getTime();
          const bHas = Number.isFinite(tb);
          const aHas = Number.isFinite(ta);
          if (aHas && bHas) return tb - ta;
          if (aHas && !bHas) return -1;
          if (!aHas && bHas) return 1;
          return b.id - a.id;
        })
        .map(p => ({ value: p.id, label: p.name })),
    [projects],
  );
  const envTypeOptions = useMemo<Option[]>(
    () => Object.values(SampleType).map(t => ({ value: t, label: friendlySampleType(t) })),
    [],
  );
  const countryOptions = useMemo<Option[]>(() => {
    if (!countriesByAlpha3) return [];
    return Object.entries(countriesByAlpha3).map(([alpha3, c]) => ({ value: alpha3, label: c.name }));
  }, [countriesByAlpha3]);
  // Removed hasSampleOptions per feedback
  const pageSizeOptions: Option[] = useMemo(() => [20, 50, 100].map(n => ({ value: n, label: String(n) })), []);

  if (error) {
    throw error instanceof Error ? error : new Error(String(error));
  }
  if (isPending) {
    return (
      <div style={{ marginBottom: theme.spacing[16] }}>
        <TitleBar
          title="Sampling Points"
          $extraWide={true}
          controls={
            <Flex gap={theme.spacing[4]}>
              <Button as={NavLink} to="/admin/merge-sampling-points" alignSelf="center">
                Merge sampling points
              </Button>
            </Flex>
          }
        />
        <VStack align="stretch" spacing={4} padding={theme.spacing[4]}>
          <Box padding={2} borderRadius={6} bg="white" boxShadow="sm">
            <Loading />
          </Box>
        </VStack>
      </div>
    );
  }

  return (
    <div style={{ marginBottom: theme.spacing[16] }}>
      <TitleBar
        title="Sampling Points"
        $extraWide={true}
        controls={
          <Flex gap={theme.spacing[4]}>
            <Button as={NavLink} to="/admin/merge-sampling-points" alignSelf="center">
              Merge sampling points
            </Button>
          </Flex>
        }
      />
      <VStack align="stretch" spacing={4} padding={theme.spacing[4]}>
        <SimpleGrid columns={{ base: 1, md: 3, lg: 4 }} spacing={theme.spacing[3]}>
          <ResistomapSelect<Option, false, GroupBase<Option>>
            options={projectOptions}
            value={projectOpt}
            onChange={opt => {
              setProjectOpt(opt);
            }}
            placeholder="Project"
            isClearable
            chakraStyles={{ control: base => ({ ...base, backgroundColor: 'white' }) }}
          />
          <Input
            placeholder="Sampling point name"
            value={envNameInput}
            onChange={e => {
              setEnvNameInput(e.target.value);
            }}
            onBlur={() => {
              const trimmed = envNameInput.trim();
              if (trimmed !== envNameFilter) {
                setEnvNameFilter(trimmed);
                setPage(1);
              }
              setEnvNameInput(trimmed);
            }}
            onKeyDown={event => {
              if (event.key === 'Enter') {
                event.preventDefault();
                const trimmed = envNameInput.trim();
                if (trimmed !== envNameFilter) {
                  setEnvNameFilter(trimmed);
                  setPage(1);
                }
                setEnvNameInput(trimmed);
              }
              if (event.key === 'Escape') {
                setEnvNameInput(envNameFilter);
              }
            }}
            bg="white"
          />
          <ResistomapSelect<Option, true, GroupBase<Option>>
            isMulti
            options={envTypeOptions}
            value={envTypes}
            onChange={opts => {
              setSampleTypes(opts as Option[]);
            }}
            placeholder="Sample type"
            chakraStyles={{ control: base => ({ ...base, backgroundColor: 'white' }) }}
            formatOptionLabel={opt => (
              <Badge colorScheme={envTypeToColorScheme(opt.value as string)}>
                {friendlySampleType(opt.value as SampleType)}
              </Badge>
            )}
          />
          <ResistomapSelect<Option, true, GroupBase<Option>>
            isMulti
            options={countryOptions}
            value={countries}
            onChange={opts => {
              setCountries(opts as Option[]);
            }}
            placeholder="Country"
            chakraStyles={{ control: base => ({ ...base, backgroundColor: 'white' }) }}
          />
          {/* Removed has samples filter per feedback */}
        </SimpleGrid>

        <Box>
          {data.rows.length === 0 ? (
            <Text>No sampling points match your filters.</Text>
          ) : (
            <VStack align="stretch" spacing={2}>
              {data.rows.map(row => (
                <Box key={row.samplingPointId} padding={2} borderRadius={6} bg="white" boxShadow="sm">
                  <Flex justify="space-between" align="center">
                    <Box>
                      <HStack spacing={3} align="baseline">
                        <HStack
                          as={Link}
                          to={`/admin/sampling-point/${row.samplingPointId}`}
                          spacing={3}
                          align="baseline"
                          style={{ textDecoration: 'none' }}
                        >
                          <Text color="gray.600">{row.samplingPointId}</Text>
                          <Text fontWeight="bold">{row.name}</Text>
                        </HStack>
                        <Badge colorScheme={envTypeToColorScheme(row.type)}>
                          {friendlySampleType(row.type as SampleType)}
                        </Badge>
                      </HStack>
                      <Text fontSize="sm" color="gray.600">
                        {row.country ?? '-'} · Samples: {row.sampleCount}
                      </Text>
                      <HStack spacing={2} mt={1} wrap="wrap">
                        {row.projects.map(p => (
                          <Badge as={Link} to={`/research/${p.id}`} key={p.id} colorScheme="green">
                            {p.name}
                          </Badge>
                        ))}
                      </HStack>
                    </Box>
                    <HStack spacing={3}>
                      <SamplesPopover samplingPointId={row.samplingPointId} sampleCount={row.sampleCount} />
                    </HStack>
                  </Flex>
                </Box>
              ))}
              <Flex justify="space-between" align="center" mt={2}>
                <Text fontSize="sm">Total: {data.total}</Text>
                <HStack>
                  <ResistomapSelect<Option, false, GroupBase<Option>>
                    options={pageSizeOptions}
                    value={pageSize}
                    onChange={opt => {
                      if (opt) setPageSize(opt);
                    }}
                    placeholder="Page size"
                    chakraStyles={{ control: base => ({ ...base, backgroundColor: 'white', width: '160px' }) }}
                  />
                  <Button
                    size="sm"
                    onClick={() => {
                      setPage(p => Math.max(1, p - 1));
                    }}
                    isDisabled={page <= 1}
                  >
                    Prev
                  </Button>
                  <Text>Page {page}</Text>
                  <Button
                    size="sm"
                    onClick={() => {
                      setPage(p => p + 1);
                    }}
                    isDisabled={data.rows.length < Number(pageSize.value)}
                  >
                    Next
                  </Button>
                </HStack>
              </Flex>
            </VStack>
          )}
        </Box>
      </VStack>
    </div>
  );
}

function SamplesPopover({ samplingPointId, sampleCount }: { samplingPointId: number; sampleCount: number }) {
  const { data } = useAdminSamplingPointSamples(samplingPointId);
  return (
    <Tooltip
      label={
        <VStack align="stretch">
          <Text fontWeight="bold">Samples ({sampleCount})</Text>
          {(data || []).map(s => (
            <HStack key={s.sampleId} spacing={2}>
              <Text color="gray.600">{s.sampleId}</Text>
              <Text>{s.number ?? '-'}</Text>
              <Badge>{s.bioRep ?? '-'}</Badge>
              <Text>chips:{s.chipsCount}</Text>
              <Text>projects:{s.projectsCount}</Text>
            </HStack>
          ))}
        </VStack>
      }
      hasArrow
      openDelay={300}
    >
      <Badge colorScheme="blue" cursor="pointer">
        Samples: {sampleCount}
      </Badge>
    </Tooltip>
  );
}
